import type {
    ICreateEditTenantPayload,
    IEditTenantPayload,
} from '@experiences/interfaces';

import type { ITenant } from '../../common/interfaces/tenant/tenant';
import type { IServiceMetadata } from '../../component/tenants/interfaces/service';
import { serviceOrder } from '../../component/tenants/TenantConstants';
import {
    axiosDelete,
    get,
    patch,
    post,
} from '../utility/Requests';

export const organizationManagementTenantUri = '/api/organization/tenant';
export const tenantAvailableServicesUri = `${organizationManagementTenantUri}/availableservices`;
export const tenantByIdUri = `${organizationManagementTenantUri}/tenantById`;

export interface IServiceStatusMap {
    [key: string]: string;
}

export interface IServiceError {
    ServiceType: string;
    ErrorCode: number;
    Description: string;
}

export function getTenants({
    organizationGuid, accountName, includeTenantServices = false,
}: { url: string; organizationGuid: string; accountName: string; includeTenantServices?: boolean }) {
    return get<ITenant[]>(organizationManagementTenantUri, {
        urlParams: {
            organizationGuid,
            accountName,
            includeTenantServices,
        },
    });
}

export function getOrgLogicalNameAndTenantName(tenantGuid: string) {
    return get<{ organizationLogicalName: string; tenantName: string }>(
        `${organizationManagementTenantUri}/orgLogicalNameAndTenantName`,
        { urlParams: { tenantGuid } },
    );
}

export function getAvailableServices({
    organizationGuid, accountName, isCanaryTenant = false,
}: { url: string; organizationGuid: string; accountName: string; isCanaryTenant?: boolean }) {
    return get<IServiceMetadata[]>(`${organizationManagementTenantUri}/availableservices`, {
        urlParams: {
            organizationGuid,
            accountName,
            isCanaryTenant,
        },
    }).then(result => result.sort((serviceA, serviceB) => serviceOrder.indexOf(serviceA.id) - serviceOrder.indexOf(serviceB.id)));
}

export function getTenantById({ id }: { url: string; id: string }) {
    return get<ITenant>(tenantByIdUri, { urlParams: { id } });
}

export function createTenant(payload: ICreateEditTenantPayload) {
    const services = payload.services;
    if (services.indexOf('bupproxyservice') === -1
        && (services.indexOf('actions') > -1 || services.indexOf('processes') > -1)) {
        services.push('bupproxyservice');
    }
    return post<ITenant>(organizationManagementTenantUri, { body: { ...payload } });
}

export function editTenant(id: string, payload: IEditTenantPayload) {
    const services = payload.services;
    if (services['actions'] || services['processes']) {
        services['bupproxyservice'] = true;
    }
    return patch<string>(organizationManagementTenantUri, {
        body: {
            id,
            ...payload,
        },
    });
}

export function deleteTenant(id: string) {
    return axiosDelete(organizationManagementTenantUri, { body: { id } });
}

export function setTenantStatus(id: string, status: boolean) {
    return post<IServiceError[]>(`${organizationManagementTenantUri}/status`, {
        body: {
            id,
            status,
        },
    });
}

export function setServiceStatus(organizationId: string, tenantId: string, services: IServiceStatusMap) {
    return patch<IServiceError[]>(`${organizationManagementTenantUri}/${organizationId}/tenants/${tenantId}/services`,
        { body: { serviceTypeServiceStatusMap: services } });
}

export function getALMEndpoint({
    organizationId, tenantId,
}: { organizationId: string; tenantId: string }) {
    return get<any>(`/organization_/api/organization/${organizationId}/tenants/sap/${tenantId}/calm`, { stripPortalPath: true }).then((data) => ({
        endpoint: data?.CALM_API_ENDPOINT,
        clientId: data?.CALM_UAA_CLIENTID,
        clientSecret: data?.CALM_UAA_CLIENTSECRET,
        serviceId: data?.UIPATH_LMS_ID,
        url: data?.CALM_UAA_URL,
    }));
}

export function updateALMEndpoint({
    organizationId, tenantId, payload,
}: { organizationId: string; tenantId: string; payload: any }) {
    return patch<any>(`/organization_/api/organization/${organizationId}/tenants/sap/${tenantId}/calm`, {
        body: {
            CustomProperties: {
                CALM_API_ENDPOINT: payload.endpoint,
                CALM_UAA_CLIENTID: payload.clientId,
                CALM_UAA_CLIENTSECRET: payload.clientSecret,
                UIPATH_LMS_ID: payload.serviceId,
                CALM_UAA_URL: payload.url,
            },
        },
        stripPortalPath: true,
    });
}

export function deleteALMEndpoint({
    organizationId, tenantId,
}: { organizationId: string; tenantId: string }) {
    // Resetting the ALM endpoint to empty strings as DELETE is not supported
    return patch<any>(`/organization_/api/organization/${organizationId}/tenants/sap/${tenantId}/calm`, {
        body: {
            CustomProperties: {
                CALM_API_ENDPOINT: '',
                CALM_UAA_CLIENTID: '',
                CALM_UAA_CLIENTSECRET: '',
                UIPATH_LMS_ID: '',
                CALM_UAA_URL: '',
            },
        },
        stripPortalPath: true,
    });
}

export function testALMEndpoint({
    organizationId, tenantId, payload,
}: { organizationId: string; tenantId: string; payload: any }) {
    return post<any>(`/${tenantId}/sapproxy_/api/calmsettings/testconnection`, {
        body: {
            CALM_API_ENDPOINT: payload.endpoint,
            CALM_UAA_CLIENTID: payload.clientId,
            CALM_UAA_CLIENTSECRET: payload.clientSecret,
            UIPATH_LMS_ID: payload.serviceId,
            CALM_UAA_URL: payload.url,
        },
        stripPortalPath: true,
    });
}
